import React from 'react';
import { LeadRow, SurfaceCut, SurfaceLead, SurfaceSource } from '../types';
import { fmtDay, pct } from '../lib';
import { Section, DAYPARTS, Dash, Empty, LeadCell, Note, Tabs, Th, td, tdBig, tdOurs, tr } from './PlatformBits';
import StagePill from './StagePill';
import SourcesList, { rowKey } from './SourcesList';

/** What the Website and Album pages share: the sources table, the cuts
 *  section and the leads section, the title badge, plus the one rule that turns the site's own ids into
 *  words. The portal never keeps a label map — a button or frame the site
 *  ships tomorrow renders here the same day, by this rule or by its id. */

export const n = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('en-IN'));

/** `web:villa-manduva` → "Villa manduva", `web:new-hero-cb` → "New hero · callback",
 *  `ask_engaged` → "Ask: engaged", `book_site_visit` → "Book site visit". */
export const prettyIntent = (id: string): string => {
  let s = id;
  const cb = s.endsWith('-cb');
  if (cb) s = s.slice(0, -3);
  s = s.replace(/^web:/, '').replace(/^ask_/, 'ask: ').replace(/[-_]/g, ' ').trim();
  s = s.charAt(0).toUpperCase() + s.slice(1);
  return cb ? `${s} · callback` : s;
};

export const DEVICE: Record<string, string> = { android: 'Android', iphone: 'iPhone', ipad: 'iPad', desktop: 'Desktop', unknown: 'Unknown' };
export const prettyDevice = (d: string | null): string => (d ? DEVICE[d] ?? d.charAt(0).toUpperCase() + d.slice(1) : '—');

export { DAYPARTS };

/** Our own surfaces wear the sidebar's line icon (lucide geometry, copied
 *  from Sidebar) as their title badge. */
const Glyph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
    strokeLinecap="round" strokeLinejoin="round" className="w-[22px] h-[22px] text-forest">
    {children}
  </svg>
);
const GLYPH = {
  website: <Glyph><circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" /></Glyph>,
  album: <Glyph><path d="M18 22H4a2 2 0 0 1-2-2V6" /><path d="m22 13-1.296-1.296a2.41 2.41 0 0 0-3.408 0L11 18" /><circle cx="12" cy="8" r="2" /><rect width="16" height="16" x="6" y="2" rx="2" /></Glyph>,
};
export const surfaceBadge = (surface: keyof typeof GLYPH) =>
  ({ icon: GLYPH[surface] });

const sumCut = (rows: SurfaceCut[]) => rows.reduce((a, r) => ({
  visitors: a.visitors + r.visitors, tapped: a.tapped + r.tapped, new_leads: a.new_leads + r.new_leads,
}), { visitors: 0, tapped: 0, new_leads: 0 });

/** Sources table — where the surface's people came from, grouped by
 *  channel. `spendOf` lets the website page price a source; the album adds
 *  who was already a lead and who watched. `selected` is `rowKey` form
 *  (`tag`, or 'direct'), matching the website page's lead filter. */
export const SourcesTable: React.FC<{
  sources: SurfaceSource[];
  album?: boolean;
  spendOf?: (s: SurfaceSource) => number | null;
  groupSpend?: (groupKey: string) => number | null;
  onSelect?: (tag: string | null) => void;
  selected?: string | null;
}> = ({ sources, album, spendOf, groupSpend, onSelect, selected }) => (
  <SourcesList
    title={album ? 'How they got to the album' : 'Where the visitors came from'}
    note={album ? 'known leads are the ones we sent it to' : selected ? 'click it again to show every source' : 'click a source to narrow the leads below'}
    rows={sources}
    people={(s) => s.visitors}
    peopleHeader={album ? 'Opened' : 'Visitors'}
    tapped={(s) => s.tapped}
    extra={album ? [{ header: 'Known leads', of: (s) => s.known_leads }, { header: 'Watched', of: (s) => s.watched, pct: true }] : []}
    spendOf={spendOf}
    groupSpend={groupSpend}
    selected={selected}
    onSelect={onSelect && ((r) => onSelect(r ? rowKey(r) : null))}
  />
);

/** Device · time of day · split test, each ending in leads. */
export const Cuts: React.FC<{ cuts: { device: SurfaceCut[]; hour: SurfaceCut[]; ab: SurfaceCut[] }; verb: string }> = ({ cuts, verb }) => {
  const [cut, setCut] = React.useState<'device' | 'hour' | 'ab'>('device');
  const rows: { label: string; visitors: number; tapped: number; new_leads: number }[] =
    cut === 'hour'
      ? DAYPARTS.map((d) => ({ label: d.label, ...sumCut(cuts.hour.filter((h) => d.has(Number(h.key)))) }))
      : cut === 'ab'
        ? cuts.ab.map((r) => ({ label: r.key === 'a' ? 'A · scrolling homepage' : r.key === 'b' ? 'B · /new' : r.key, ...r }))
        : cuts.device.map((r) => ({ label: prettyDevice(r.key), ...r }));
  const tabs: [typeof cut, string][] = [['device', 'Device'], ['hour', 'Time of day'], ...(cuts.ab.length ? [['ab', 'Split test'] as [typeof cut, string]] : [])];
  return (
    <Section title="Who and when" note={<Tabs value={cut} onChange={setCut} items={tabs} />}>
      <table className="w-full min-w-[640px] border-collapse">
        <thead><tr><Th left>{cut === 'device' ? 'Device' : cut === 'hour' ? 'Time (IST)' : 'Version'}</Th><Th>{verb}</Th><Th>Tapped</Th><Th>Tap rate</Th><Th ours>New leads</Th><Th ours>Lead rate</Th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className={tr}>
              <td className="px-3 py-2.5 font-semibold whitespace-nowrap">{r.label}</td>
              <td className={tdBig}>{n(r.visitors)}</td>
              <td className={`${td} text-right`}>{r.tapped || <Dash />}</td>
              <td className={`${td} text-right`}>{r.tapped ? pct(r.tapped, r.visitors) : <Dash />}</td>
              <td className={tdOurs}>{r.new_leads || <Dash />}</td>
              <td className={`${td} text-right`}>{r.new_leads ? `${(r.new_leads / r.visitors * 100).toFixed(2)}%` : <Dash />}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {cut === 'ab' && <Note>Phones only.</Note>}
    </Section>
  );
};

/** The leads section. `extra` renders the surface's own columns for a lead;
 *  `filter` names the source the list is narrowed to, with a way back. */
export const SurfaceLeads: React.FC<{
  title: string; leads: SurfaceLead[]; names: Map<string, LeadRow>;
  extraHeaders: string[]; extra: (l: SurfaceLead) => React.ReactNode[];
  filter?: { label: string; onClear: () => void } | null;
}> = ({ title, leads, names, extraHeaders, extra, filter }) => (
  <Section title={title} note={
    <span className="inline-flex items-center gap-2">
      {filter && (
        <span className="inline-flex items-center gap-1.5 h-6 pl-2.5 pr-1 rounded-full bg-sage/30 text-forest text-[12px] font-semibold">
          from {filter.label}
          <button onClick={filter.onClear} aria-label="Show every source"
                  className="w-4 h-4 grid place-items-center rounded-full hover:bg-forest/10 leading-none">×</button>
        </span>
      )}
      <span>{leads.length} lead{leads.length === 1 ? '' : 's'}</span>
    </span>
  }>
    <table className="w-full min-w-[960px] border-collapse">
      <thead><tr><Th left>Lead</Th><Th left>Came in</Th><Th left>Arrived from</Th>{extraHeaders.map((h) => <Th key={h} left>{h}</Th>)}<Th left>Stage</Th></tr></thead>
      <tbody>
        {leads.length === 0 && <Empty cols={5 + extraHeaders.length}>{filter ? 'No leads from this source in this window.' : 'No leads in this window.'}</Empty>}
        {leads.map((l) => (
          <tr key={l.lead_id} className={tr}>
            <LeadCell id={l.lead_id} name={names.get(l.lead_id)?.name ?? 'open lead'}
                      sub={<>{names.get(l.lead_id)?.mobile ?? ''}{!l.new_lead ? ' · already a lead' : ''}</>} />
            <td className="px-3 py-2.5 whitespace-nowrap text-graphite">{l.lead_created_at ? fmtDay(l.lead_created_at) : <Dash />}</td>
            <td className="px-3 py-2.5 text-graphite text-[12.5px] max-w-[220px] truncate" title={l.source_label}>{l.source_label}</td>
            {extra(l).map((cell, i) => <td key={i} className="px-3 py-2.5 text-[12.5px] whitespace-nowrap">{cell}</td>)}
            <td className="px-3 py-2.5 whitespace-nowrap"><StagePill stage={l.stage} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </Section>
);
